/**
 * Presentational scalar-field grid for the dashboard collector edit form
 * (naam / verdieping / blok / m² / systeem / legpatroon + materiaallijst).
 *
 * Split out of DashboardCollectorEditForm to keep that orchestration component
 * under the file-size budget. Stateless: the parent owns all state and passes
 * values + change handlers down. Markup replicated from CollectorEditForm.
 */
import type { CollectorPicking } from '@/api/tasks';
import { formatPickingOption } from '@/utils/collectors';
import { useCollectorSystems } from '@/stores/systems';

export function DashboardCollectorFields({
  collectorId,
  name,
  onName,
  verdieping,
  onVerdieping,
  blok,
  onBlok,
  m2,
  onM2,
  systeem,
  onSysteem,
  legpatroon,
  onLegpatroon,
  materiaallijstId,
  onMateriaallijst,
  existingBloks,
  existingVerdiepingen,
  pickings,
}: {
  collectorId: number;
  name: string;
  onName: (v: string) => void;
  verdieping: string;
  onVerdieping: (v: string) => void;
  blok: string;
  onBlok: (v: string) => void;
  m2: string;
  onM2: (v: string) => void;
  systeem: string;
  onSysteem: (v: string) => void;
  legpatroon: string;
  onLegpatroon: (v: string) => void;
  materiaallijstId: number | null;
  onMateriaallijst: (v: number | null) => void;
  existingBloks: string[];
  existingVerdiepingen: string[];
  pickings: CollectorPicking[];
}) {
  // Options come from the Odoo-defined roster (stores/systems), not a list kept here.
  const systeemOptions = useCollectorSystems();
  return (
    <>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        <div>
          <label htmlFor={`dash-edit-naam-${collectorId}`} className="block text-xs font-semibold text-td-muted mb-1">Naam</label>
          <input id={`dash-edit-naam-${collectorId}`} type="text" value={name} onChange={(e) => onName(e.target.value)}
            className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-td-blue" />
        </div>

        <div>
          <label htmlFor={`dash-edit-verdieping-${collectorId}`} className="block text-xs font-semibold text-td-muted mb-1">Verdieping</label>
          <input id={`dash-edit-verdieping-${collectorId}`} type="text" list={`dash-edit-verdieping-opts-${collectorId}`} autoComplete="one-time-code" name="dash-edit-vrdpng-nofill"
            value={verdieping} onChange={(e) => onVerdieping(e.target.value)} placeholder="+00"
            className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-td-blue" />
          <datalist id={`dash-edit-verdieping-opts-${collectorId}`}>
            {existingVerdiepingen.map((v) => <option key={v} value={v} />)}
          </datalist>
        </div>

        <div>
          <label htmlFor={`dash-edit-blok-${collectorId}`} className="block text-xs font-semibold text-td-muted mb-1">Blok</label>
          <input id={`dash-edit-blok-${collectorId}`} type="text" list={`dash-edit-blok-opts-${collectorId}`} autoComplete="one-time-code" name="dash-edit-blk-nofill"
            value={blok} onChange={(e) => onBlok(e.target.value)} placeholder="Hoofdgebouw"
            className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-td-blue" />
          <datalist id={`dash-edit-blok-opts-${collectorId}`}>
            {existingBloks.map((b) => <option key={b} value={b} />)}
          </datalist>
        </div>

        <div>
          <label htmlFor={`dash-edit-m2-${collectorId}`} className="block text-xs font-semibold text-td-muted mb-1">m&sup2;</label>
          <input id={`dash-edit-m2-${collectorId}`} type="number" step="0.1" min="0" value={m2} onChange={(e) => onM2(e.target.value)}
            className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-td-blue" />
        </div>

        <div>
          <label htmlFor={`dash-edit-systeem-${collectorId}`} className="block text-xs font-semibold text-td-muted mb-1">Systeem</label>
          <select id={`dash-edit-systeem-${collectorId}`} value={systeem} onChange={(e) => onSysteem(e.target.value)}
            className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-td-blue bg-white">
            {/* A value the roster does not know (legacy data, or it has not loaded)
                stays selectable so editing another field cannot change the system. */}
            {!systeemOptions.some((s) => s.code === systeem) && (
              <option value={systeem}>{systeem || '—'}</option>
            )}
            {systeemOptions.map((s) => (
              <option key={s.code} value={s.code}>{s.label}</option>
            ))}
          </select>
        </div>

        <div>
          <label className="block text-xs font-semibold text-td-muted mb-1">Legpatroon</label>
          <div className="flex items-center gap-1.5 flex-wrap">
            {['10', '15', '20'].map((v) => (
              <button key={v} type="button" onClick={() => onLegpatroon(v)}
                className={`px-2.5 py-1.5 text-xs font-semibold rounded-lg border transition-colors ${legpatroon === v ? 'bg-td-blue text-white border-td-blue' : 'border-gray-300 text-td-navy hover:bg-gray-50'}`}
              >{v}</button>
            ))}
            <button type="button" onClick={() => onLegpatroon('custom')}
              className={`px-2.5 py-1.5 text-xs font-semibold rounded-lg border transition-colors ${legpatroon.startsWith('custom:') ? 'bg-td-blue text-white border-td-blue' : 'border-gray-300 text-td-navy hover:bg-gray-50'}`}
            >{legpatroon.startsWith('custom:') ? legpatroon.split(':')[1] : 'Ander'}</button>
            <button type="button" onClick={() => onLegpatroon('meerdere')}
              className={`px-2.5 py-1.5 text-xs font-semibold rounded-lg border transition-colors ${legpatroon === 'meerdere' ? 'bg-td-blue text-white border-td-blue' : 'border-gray-300 text-td-navy hover:bg-gray-50'}`}
            >Meerdere</button>
          </div>
        </div>
      </div>

      {pickings.length > 0 && (
        <div className="mt-4">
          <label htmlFor={`dash-edit-materiaallijst-${collectorId}`} className="block text-xs font-semibold text-td-muted mb-1">Materiaallijst</label>
          <select
            id={`dash-edit-materiaallijst-${collectorId}`}
            value={materiaallijstId ?? ''}
            onChange={(e) => onMateriaallijst(Number(e.target.value) || null)}
            className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-td-blue bg-white"
          >
            <option value="">Geen materiaallijst</option>
            {pickings.map((p) => (
              <option key={p.id} value={p.id}>{formatPickingOption(p)}</option>
            ))}
          </select>
        </div>
      )}
    </>
  );
}
